import type { OnInit } from '@angular/core';
import { Component, Input } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import type { CraftData, PageDataResponse } from '@core-mkt/resolvers/page-data/page-data-response';
import { AdaTokenService } from '@core-mkt/services/ada-token/ada-token.service';
import { AnalyticsInitializerService } from '@core-mkt/services/analytics/analytics-initializer.service';
import { CjAffiliateService } from '@core-mkt/services/cj-affiliate/cj-affiliate.service';
import { EnvService } from '@core-mkt/services/env/env.service';
import type { SchemaOrgNode } from '@core-mkt/services/schema/schema-org-node.model';
import { SchemaService } from '@core-mkt/services/schema/schema.service';
import { stripJsonComments } from '@core-mkt/services/schema/strip-json-comments.util';
import { getWebPageSchema, getWebsiteSchema } from '@core-mkt/services/schema/website.schema';
import { log, SEVERITY } from '@core-mkt/shared/lib/logger';
import { ScriptLoaderService } from '@core-mkt/services/script-loader/script-loader.service';
import { RequestService } from '@core-mkt/services/url/url.service';
import { cloneDeep } from 'lodash-es';

@Component({
  selector: 'marketing',
  template: '',
})
export class MarketingComponent implements OnInit {
  @Input() includeOptimizely: boolean;
  @Input() optimizelyIdOverride?: string;
  @Input() segmentIdOverride?: string;
  resolverData: PageDataResponse;
  pageData: CraftData;
  isOptimizelyEnabled: boolean;
  isSegmentEnabled: boolean;
  private seoUrl: string;

  public constructor(
    private sls: ScriptLoaderService,
    private analyticsInitializer: AnalyticsInitializerService,
    private cjAffiliateService: CjAffiliateService,
    private adaTokenService: AdaTokenService,
    private route: ActivatedRoute,
    private request: RequestService,
    private env: EnvService,
    private schemaService: SchemaService,
  ) {
    this.resolverData = cloneDeep(this.route.snapshot.data.pageData as PageDataResponse);
    this.pageData = this.resolverData.craftData;
  }

  ngOnInit(): void {
    // Ensure that the Optimizely and Segment scripts are only injected if the IDs are present in the environment
    this.isOptimizelyEnabled = !!this.env.get.optimizelyId;
    this.isSegmentEnabled =
      !!this.env.get.segmentId && this.env.get.segmentId !== null && this.env.get.segmentId !== '';
    if (this.includeOptimizely && this.isOptimizelyEnabled) {
      this.sls.injectScriptByType('optimizely', this.optimizelyIdOverride);
    }
    if (this.isSegmentEnabled) {
      void this.analyticsInitializer.initialize(this.segmentIdOverride);
    }

    // Conditionally set CJEVENTID cookie
    this.cjAffiliateService.conditionallySetCookie();

    // ADA Chatbot
    const adaChatbotId = this.pageData?.pageOptions?.adaChatbot || this.env.get.brandConfig?.defaultAda;

    if (adaChatbotId && adaChatbotId !== '') {
      this.sls.injectScriptByType('ada', adaChatbotId);
      this.adaTokenService.setAdaToken();
    }

    // Structured data is collected by SchemaService and emitted as a single
    // <script type="application/ld+json"> at finalize() in the template (EPS-3951).
    // Register the page-level WebSite/WebPage nodes plus any CMS-authored structured
    // data here instead of injecting separate ld+json scripts, which previously caused
    // duplicate schema scripts to co-exist with the SchemaService graph.
    this.registerStructuredData();
  }

  /**
   * Registers the page-level WebSite and WebPage nodes (migrated from the legacy
   * `dynamicStructuredData` script) plus any CMS-authored structured data into the
   * shared SchemaService graph. SchemaService is a no-op on the browser, so these
   * nodes are emitted server-side in the single graph (consistent with all other
   * component schema on this template).
   */
  private registerStructuredData(): void {
    const website = getWebsiteSchema({
      brandName: this.env.get.brandConfig?.name,
      brandUrl: this.env.get.brandConfig?.url,
    });
    if (website) {
      this.schemaService.register(website);
    }

    const webPage = getWebPageSchema({
      pageTitle: this.pageData?.seoContent?.pageTitle,
      pageUrl: this.pageData?.seoContent?.ogUrl,
      pageDescription: this.pageData?.seoContent?.description,
    });
    if (webPage) {
      this.schemaService.register(webPage);
    }

    this.registerCmsStructuredData();
  }

  /**
   * Registers CMS-authored structured data (Craft `structuredData` field) into the
   * shared graph as authoritative — on an `@id` collision with a component-emitted
   * node the CMS values win (deep-merged), regardless of registration order
   * (EPS-4243). Accepts either a single node or a `{ '@graph': [...] }` wrapper; the
   * top-level `@context` is dropped because SchemaService emits one at the graph root.
   * JSONC-style comments are tolerated (stripped and re-parsed) because Google's own
   * structured-data examples contain them; irrecoverably malformed JSON is skipped —
   * invalid JSON-LD has no SEO value — but every skip or rescue is WARN-logged so
   * authoring problems are diagnosable instead of vanishing silently (EPS-4335).
   */
  private registerCmsStructuredData(): void {
    const raw: string | undefined = this.pageData?.structuredData?.structuredData;
    if (!raw) return;

    let parsed: Record<string, unknown>;
    try {
      parsed = JSON.parse(raw) as Record<string, unknown>;
    } catch (parseError) {
      try {
        parsed = JSON.parse(stripJsonComments(raw)) as Record<string, unknown>;
        this.warnCmsStructuredData(
          'CMS structured data contains JSON comments',
          43351,
          'recovered by stripping comments',
        );
      } catch {
        this.warnCmsStructuredData('CMS structured data is not valid JSON', 43352, (parseError as Error).message);
        return;
      }
    }

    const graph = parsed?.['@graph'];
    if (Array.isArray(graph)) {
      // Validate CMS-authored nodes: keep only plain objects with a string
      // `@type`, and strip per-node `@context`. A malformed array (non-objects,
      // or nodes missing `@type`) would otherwise land in the consolidated
      // `@graph` and break rich-results validation.
      const nodes = graph.map((entry) => sanitizeCmsNode(entry)).filter((node): node is SchemaOrgNode => node !== null);
      if (nodes.length < graph.length) {
        this.warnCmsStructuredData(
          'CMS structured data nodes failed validation',
          43353,
          `${graph.length - nodes.length} of ${
            graph.length
          } @graph nodes dropped (not a plain object with a string @type)`,
        );
      }
      if (nodes.length > 0) {
        this.schemaService.registerAuthoritative(nodes);
      }
      return;
    }

    const node = sanitizeCmsNode(parsed);
    if (node) {
      this.schemaService.registerAuthoritative(node);
      return;
    }
    this.warnCmsStructuredData(
      'CMS structured data node failed validation',
      43354,
      'not a plain object with a string @type',
    );
  }

  /** WARN-logs a CMS structured-data authoring problem so it is visible in SSR monitoring (EPS-4335). */
  private warnCmsStructuredData(message: string, code: number, detail: string): void {
    log({
      severity: SEVERITY.WARN,
      message,
      code,
      detail,
      contextId: this.pageData?.entryData?.slug,
      brandId: this.env.get.brandConfig?.craftSite,
      tags: ['MarketingComponent.registerCmsStructuredData'],
    });
  }
}

/**
 * Returns a registrable Schema.org node from a CMS-authored value, or `null`
 * when the value is not a plain object with a string `@type`. Strips the
 * per-node `@context` since SchemaService emits one at the graph root.
 */
function sanitizeCmsNode(entry: unknown): SchemaOrgNode | null {
  if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return null;
  const record = entry as Record<string, unknown>;
  if (typeof record['@type'] !== 'string') return null;
  const node: Record<string, unknown> = { ...record };
  delete node['@context'];
  return node as SchemaOrgNode;
}
